import { Zone, ZoneCurves, ZoneValues } from '../../@types/projects/types';
import { AnalyzeType } from '../../@types/samples/types';
import { roundBrooksCoreyPoreSizeDistributionIndex } from '../../utils/brooksCorey';
import { ResultData, TableData, TablePropData } from './types';

export function getChartData(zones: Zone[], analysis_type: AnalyzeType) {
  return zones.map(zone => {
    const color = zone.color;
    const zoneCurves =
      analysis_type === AnalyzeType.LIAO
        ? zone?.zone_curves?.liao_curves
        : zone?.zone_curves?.lambda_curves;

    function getCurveDataBySw(curve: keyof Omit<ZoneCurves, 'sw'>) {
      return (
        zoneCurves?.[curve].map((data, index) => {
          return [zoneCurves.sw[index], data];
        }) || []
      );
    }

    function getCurveDataBySg(curve: keyof Omit<ZoneCurves, 'sg'>) {
      return (
        zoneCurves?.[curve].map((data, index) => {
          return [zoneCurves.sg[index], data];
        }) || []
      );
    }

    return {
      color,
      pcowXsw: getCurveDataBySw('pcow' as keyof ZoneCurves),
      heightXsw: getCurveDataBySw('height' as keyof ZoneCurves),
      krwXsw: getCurveDataBySw('krw' as keyof ZoneCurves),
      krowXsw: getCurveDataBySw('krow' as keyof ZoneCurves),
      krgXsw: getCurveDataBySw('krg' as keyof ZoneCurves),
      krogXsw: getCurveDataBySw('krog' as keyof ZoneCurves),
      krgXsg: getCurveDataBySg('krg' as keyof ZoneCurves),
      krogXsg: getCurveDataBySg('krog' as keyof ZoneCurves),
      krwscaledXsw: getCurveDataBySw('krw_scaled' as keyof ZoneCurves),
      krowscaledXsw: getCurveDataBySw('krow_scaled' as keyof ZoneCurves),
      krgscaledXsg: getCurveDataBySg('krg_scaled' as keyof ZoneCurves),
      krogscaledXsg: getCurveDataBySg('krog_scaled' as keyof ZoneCurves),
    };
  });
}

export function createSeries(
  name: string,
  data: number[][],
  color: string | null,
  lineWidth = 2
) {
  return {
    name,
    type: 'line',
    data: data || [],
    color,
    lineWidth
  } as ResultData;
}

export function getTableData(
  zones: Zone[],
  analysisType: AnalyzeType = AnalyzeType.LIAO
): TableData {
  function getZonesProp(prop: 'mean' | 'start' | 'end') {
    const obj: TablePropData = {};
    zones.forEach((zone, index) => {
      obj[`fu${index + 1}`] = zone[prop] ?? null;
    });

    return obj;
  }

  function getZoneValue(prop: keyof NonNullable<ZoneValues['liao_values']>) {
    const obj: TablePropData = {};
    zones.forEach((zone, index) => {
      const values =
        analysisType === AnalyzeType.LIAO
          ? zone.zone_values?.liao_values
          : zone.zone_values?.lambda_values;
      obj[`fu${index + 1}`] = values?.[prop] ?? null;
    });

    return obj;
  }

  const fzi = getZonesProp('mean');
  const fziMin = getZonesProp('start');
  const fziMax = getZonesProp('end');
  const isLiao = analysisType === AnalyzeType.LIAO;
  const a = getZoneValue(isLiao ? 'best_a' : 'pe');
  const b = getZoneValue(isLiao ? 'best_b' : 'lambda_value');
  const lambda = isLiao
    ? getZoneValue('lambda_variable')
    : (() => {
        const obj: TablePropData = {};
        zones.forEach((zone, index) => {
          const exponent = zone.zone_values?.lambda_values?.lambda_value;
          obj[`fu${index + 1}`] =
            roundBrooksCoreyPoreSizeDistributionIndex(exponent);
        });
        return obj;
      })();
  const pce = isLiao ? getZoneValue('pce') : {};
  const pcmax_ow = getZoneValue('true_pc_max');
  const swir = getZoneValue('swir');
  const no_w = getZoneValue('no_w');
  const nw = getZoneValue('nw');
  const krw = getZoneValue('krw_absolute');
  const sorw = getZoneValue('sor_w');
  const kro = getZoneValue('kro_absolute');
  const no_g = getZoneValue('no_g');
  const ng = getZoneValue('ng');
  const krg = getZoneValue('krg_absolute');
  const sgc = getZoneValue('sgc');
  const sor = getZoneValue('sor');
  const sgmax = getZoneValue('sgmax');
  const sorg = getZoneValue('sor_g');
  const pcmax_og = getZoneValue('pc_max_og');

  return {
    fzi,
    fziMin,
    fziMax,
    a,
    b,
    lambda,
    pce,
    pcmax_ow,
    swir,
    no_w,
    nw,
    krw,
    sorw,
    kro,
    no_g,
    ng,
    krg,
    sgc,
    sor,
    sgmax,
    sorg,
    pcmax_og
  };
}

export function parseFUTableColumns(zones?: Zone[]) {
  const defaultColumns = [
    {
      title: 'Rock Type',
      dataIndex: 'flowUnit',
      key: 'flowUnit'
    }
  ];
  let fuColumns: { title: JSX.Element; dataIndex: string; key: string }[] = [];

  if (zones?.length) {
    fuColumns = zones.map((zone, index) => {
      const name = `fu${index + 1}`;
      return {
        title: (
          <span
            style={{
              color: zone.color || undefined,
              textTransform: 'uppercase'
            }}
          >
            {name}
          </span>
        ),
        dataIndex: name,
        key: name
      };
    });
  }

  return [...defaultColumns, ...fuColumns];
}

export function parseFUTableData(
  tableData: TableData,
  xAxisLabel = 'FZI',
  analysisType: AnalyzeType = AnalyzeType.LIAO
) {
  const fuKeys = Object.assign(
    {},
    ...Object.keys(tableData.fzi).map(key => ({ [key]: '' }))
  );
  return [
    {
      key: '1',
      flowUnit: <span style={{ fontWeight: 'bold' }}>{xAxisLabel}</span>,
      ...tableData.fzi
    },
    {
      key: '2',
      flowUnit: `${xAxisLabel} Min`,
      ...tableData.fziMin
    },
    {
      key: '3',
      flowUnit: `${xAxisLabel} Max`,
      ...tableData.fziMax
    },
    {
      key: '4',
      flowUnit: <span style={{ fontWeight: 'bold' }}>Water-Oil System</span>,
      ...fuKeys,
      children: [
        ...(analysisType === AnalyzeType.LIAO
          ? [
              { key: '4-1', flowUnit: 'a', ...tableData.a },
              { key: '4-2', flowUnit: 'b', ...tableData.b },
              {
                key: '4-3',
                flowUnit: 'Liao lambda',
                ...tableData.lambda
              },
              { key: '4-4', flowUnit: 'Pce', ...tableData.pce }
            ]
          : [
              { key: '4-1', flowUnit: 'Entry pressure, Pe', ...tableData.a },
              {
                key: '4-2',
                flowUnit: 'Fitted exponent magnitude, −m',
                ...tableData.b
              },
              {
                key: '4-3',
                flowUnit: 'Pore-size index, λBC',
                ...tableData.lambda
              }
            ]),
        {
          key: '4-5',
          flowUnit: 'Pcmax_ow',
          ...tableData.pcmax_ow
        },
        {
          key: '4-6',
          flowUnit: 'Swir',
          ...tableData.swir
        },
        {
          key: '4-7',
          flowUnit: 'No',
          ...tableData.no_w
        },
        {
          key: '4-8',
          flowUnit: 'Nw',
          ...tableData.nw
        },
        {
          key: '4-9',
          flowUnit: 'Krw',
          ...tableData.krw
        },
        {
          key: '4-10',
          flowUnit: 'Sor',
          ...tableData.sorw
        },
        {
          key: '4-11',
          flowUnit: 'Kro',
          ...tableData.kro
        }
      ]
    },

    {
      key: '5',
      flowUnit: <span style={{ fontWeight: 'bold' }}>Gas-Oil System</span>,
      fu1: '',
      fu2: '',
      fu3: '',
      fu4: '',
      children: [
        {
          key: '5-1',
          flowUnit: 'Nog',
          ...tableData.no_g
        },
        {
          key: '5-2',
          flowUnit: 'Ng',
          ...tableData.ng
        },
        {
          key: '5-3',
          flowUnit: 'Krg',
          ...tableData.krg
        },
        {
          key: '5-4',
          flowUnit: 'Sgc',
          ...tableData.sgc
        },
        {
          key: '5-5',
          flowUnit: 'Sor',
          ...tableData.sor
        },
        {
          key: '5-6',
          flowUnit: 'Sgmax',
          ...tableData.sgmax
        },
        {
          key: '5-7',
          flowUnit: 'Sor_g',
          ...tableData.sorg
        },
        {
          key: '5-8',
          flowUnit: 'Pcmax_og',
          ...tableData.pcmax_og
        }
      ]
    }
  ];
}

export const isInvalidFraction = (value: any) => {
  const num = parseFloat(value);
  return isNaN(num) || num < 0 || num > 1;
};

export const isInvalidNumber = (value: any) => {
  const num = parseFloat(value);
  return isNaN(num) || num < 0;
};
